/**
 * The list of event icons and everything about them that is not a drawing.
 *
 * Split out of components/EventIcon.jsx for one reason: the drawings are JSX
 * and JSX cannot be imported by a test that runs in plain node. This half —
 * which icons exist, what they are called in sixteen languages, and the rules
 * for putting one on the front of a title and taking it off again — is the
 * half where a mistake is silent, so it is the half that has to be testable.
 * See test/eventIcons.test.mjs.
 */

/** The order they are offered in: the eight in daily use first. */
export const ICONS = [
  '🎂', '🩺', '🐾', '🏋️', '💼', '🍽️', '✈️', '🛒',
  '🎉', '👥', '🎓', '🚗', '🏠', '📞', '🎵', '🎬',
  '☕', '🏦', '✂️', '🦷', '🎁', '🏖️', '❤️', '👪',
];

/** Characters that used to mean one of these, and still do. */
export const LEGACY = { '🏃': '🏋️', '🏋': '🏋️', '🍽': '🍽️', '✈': '✈️', '🏖': '🏖️', '❤': '❤️', '👨‍👩‍👧‍👦': '👪' };

const NAMES = {
  it: ['Compleanno','Dottore','Veterinario','Sport','Lavoro','Cena','Viaggio','Spesa','Festa','Riunione','Scuola','Auto','Casa','Telefonata','Musica','Cinema','Caffè','Banca','Parrucchiere','Dentista','Regalo','Vacanza','Anniversario','Famiglia'],
  en: ['Birthday','Doctor','Vet','Sport','Work','Dinner','Travel','Shopping','Party','Meeting','School','Car','Home','Call','Music','Cinema','Coffee','Bank','Hairdresser','Dentist','Gift','Holiday','Anniversary','Family'],
  es: ['Cumpleaños','Médico','Veterinario','Deporte','Trabajo','Cena','Viaje','Compras','Fiesta','Reunión','Escuela','Coche','Casa','Llamada','Música','Cine','Café','Banco','Peluquería','Dentista','Regalo','Vacaciones','Aniversario','Familia'],
  de: ['Geburtstag','Arzt','Tierarzt','Sport','Arbeit','Abendessen','Reise','Einkauf','Feier','Besprechung','Schule','Auto','Zuhause','Anruf','Musik','Kino','Kaffee','Bank','Friseur','Zahnarzt','Geschenk','Urlaub','Jahrestag','Familie'],
  fr: ['Anniversaire','Médecin','Vétérinaire','Sport','Travail','Dîner','Voyage','Courses','Fête','Réunion','École','Voiture','Maison','Appel','Musique','Cinéma','Café','Banque','Coiffeur','Dentiste','Cadeau','Vacances','Anniversaire de mariage','Famille'],
  nl: ['Verjaardag','Dokter','Dierenarts','Sport','Werk','Diner','Reis','Boodschappen','Feest','Vergadering','School','Auto','Thuis','Telefoontje','Muziek','Bioscoop','Koffie','Bank','Kapper','Tandarts','Cadeau','Vakantie','Jubileum','Gezin'],
  pt: ['Aniversário','Médico','Veterinário','Desporto','Trabalho','Jantar','Viagem','Compras','Festa','Reunião','Escola','Carro','Casa','Telefonema','Música','Cinema','Café','Banco','Cabeleireiro','Dentista','Presente','Férias','Aniversário de casamento','Família'],
  'pt-BR': ['Aniversário','Médico','Veterinário','Esporte','Trabalho','Jantar','Viagem','Compras','Festa','Reunião','Escola','Carro','Casa','Telefonema','Música','Cinema','Café','Banco','Cabeleireiro','Dentista','Presente','Férias','Aniversário de casamento','Família'],
  ru: ['День рождения','Врач','Ветеринар','Спорт','Работа','Ужин','Поездка','Покупки','Праздник','Встреча','Школа','Машина','Дом','Звонок','Музыка','Кино','Кофе','Банк','Парикмахер','Стоматолог','Подарок','Отпуск','Годовщина','Семья'],
  pl: ['Urodziny','Lekarz','Weterynarz','Sport','Praca','Kolacja','Podróż','Zakupy','Impreza','Spotkanie','Szkoła','Samochód','Dom','Telefon','Muzyka','Kino','Kawa','Bank','Fryzjer','Dentysta','Prezent','Wakacje','Rocznica','Rodzina'],
  ro: ['Zi de naștere','Doctor','Veterinar','Sport','Muncă','Cină','Călătorie','Cumpărături','Petrecere','Ședință','Școală','Mașină','Acasă','Telefon','Muzică','Cinema','Cafea','Bancă','Coafor','Dentist','Cadou','Vacanță','Aniversare','Familie'],
  zh: ['生日','看医生','兽医','运动','工作','晚餐','出行','购物','聚会','会议','学校','汽车','家','电话','音乐','电影','咖啡','银行','理发','牙医','礼物','假期','纪念日','家人'],
  ja: ['誕生日','通院','動物病院','運動','仕事','夕食','旅行','買い物','パーティー','会議','学校','車','家','電話','音楽','映画','カフェ','銀行','美容院','歯医者','プレゼント','休暇','記念日','家族'],
  hi: ['जन्मदिन','डॉक्टर','पशु चिकित्सक','खेल','काम','रात का खाना','यात्रा','खरीदारी','पार्टी','बैठक','स्कूल','कार','घर','कॉल','संगीत','सिनेमा','कॉफ़ी','बैंक','नाई','दंत चिकित्सक','उपहार','छुट्टी','सालगिरह','परिवार'],
  ko: ['생일','병원','동물병원','운동','업무','저녁 식사','여행','장보기','파티','회의','학교','자동차','집','전화','음악','영화','커피','은행','미용실','치과','선물','휴가','기념일','가족'],
  ar: ['عيد ميلاد','طبيب','طبيب بيطري','رياضة','عمل','عشاء','سفر','تسوّق','حفلة','اجتماع','مدرسة','سيارة','المنزل','مكالمة','موسيقى','سينما','قهوة','بنك','حلاق','طبيب أسنان','هدية','إجازة','ذكرى سنوية','عائلة'],
};

export const ICON_HEADING = {
  it: 'Icona', en: 'Icon', es: 'Icono', de: 'Symbol', fr: 'Icône', nl: 'Pictogram',
  pt: 'Ícone', 'pt-BR': 'Ícone', ru: 'Значок', pl: 'Ikona', ro: 'Pictogramă', zh: '图标',
  ja: 'アイコン', hi: 'आइकन', ko: '아이콘', ar: 'أيقونة',
};

/** The names of all icons, in the language asked for. */
export function iconNames(language) {
  const raw = String(language || 'it');
  return NAMES[raw] || NAMES[raw.split('-')[0]] || NAMES.en;
}

/** The icon this title already starts with, in its current spelling, or ''. */
export function iconOf(title) {
  const first = String(title || '').trimStart();
  const found = ICONS.find(i => first.startsWith(i));
  if (found) return found;
  // An older spelling still counts as that icon — otherwise every event saved
  // before today would quietly lose its picture.
  const old = Object.keys(LEGACY).find(k => first.startsWith(k));
  return old ? LEGACY[old] : '';
}

/** How many characters of the title the icon takes up (0 if there is none). */
function iconLength(title) {
  const first = String(title || '').trimStart();
  const found = ICONS.find(i => first.startsWith(i));
  if (found) return found.length;
  const old = Object.keys(LEGACY).find(k => first.startsWith(k));
  return old ? old.length : 0;
}

/**
 * Every spelling of one icon: the current one, and the older characters that
 * still mean it. Sport was 🏃 before it was the barbell, and an event saved
 * then must still turn up when you ask for everything with that icon.
 *
 * The list is built here, where LEGACY lives, and sent to the server as a
 * plain list to match against — so the mapping stays in one file and the
 * server never has to know that a running man used to be a barbell.
 */
export function aliasesFor(icon) {
  const out = [icon];
  for (const [old, now] of Object.entries(LEGACY)) if (now === icon) out.push(old);
  return out;
}

/** The title without its icon — what MeMo shows beside the drawing. */
export function stripIcon(title) {
  const n = iconLength(title);
  // trimStart in both branches: a title made only of spaces has to come back
  // empty, or putting an icon on it produces '🎂    ' and the event looks named.
  return n ? String(title).trimStart().slice(n).trimStart() : String(title || '').trimStart();
}

/** The title with `icon` on the front — and never two of them. */
export function withIcon(title, icon) {
  const rest = stripIcon(title);
  const had = iconOf(title);
  if (!icon || icon === had) return rest;      // tapping the same one takes it off
  return icon + ' ' + rest;
}

